import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PageHeader, Card, Field, Loading } from "../../components/common";
import { api, errMsg, mediaUrl } from "../../lib/api";
import { pkr } from "../../lib/format";

const EMPTY = { name: "", category: "", brand_name: "", brand_logo: "", description: "", price: "", commission_type: "fixed", commission_value: "", availability: "in_stock",
  stock_qty: "", requires_delivery: true, delivery_info: "", terms: "", main_image: "", images: [], video_url: "", video_file: "" };

function UploadButton({ accept, onDone, testid, label }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd);
      onDone(data.path);
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false); e.target.value = "";
  };
  return (<>
    <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
    <Button type="button" variant="outline" disabled={busy} onClick={() => ref.current.click()} data-testid={testid}><Upload className="mr-2 h-4 w-4" />{busy ? "Uploading…" : label}</Button>
  </>);
}

export default function DealEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(id ? null : EMPTY);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (id) api.get(`/deals/${id}`).then((r) => { setMeta(r.data); setF({ ...EMPTY, ...r.data, stock_qty: r.data.stock_qty ?? "" }); }); }, [id]);
  if (!f) return <Loading />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const price = parseInt(f.price) || 0;
  const cval = parseFloat(f.commission_value) || 0;
  const preview = f.commission_type === "fixed" ? cval : Math.round((price * cval) / 100);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { ...f, price, commission_value: cval, stock_qty: f.stock_qty === "" ? null : parseInt(f.stock_qty) };
    ["id", "ref", "status", "created_at", "updated_at", "created_by"].forEach((k) => delete body[k]);
    try {
      if (id) await api.put(`/admin/deals/${id}`, body); else await api.post("/admin/deals", body);
      toast.success(id ? "Deal updated. Existing orders keep their original price and commission." : "Deal created as draft. Publish it from Manage Deals.");
      nav("/admin/deals");
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };

  return (
    <div className="max-w-4xl">
      <Link to="/admin/deals" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-600"><ArrowLeft className="h-4 w-4" />Manage deals</Link>
      <PageHeader title={id ? `Edit ${meta?.ref}` : "New deal"} subtitle={id ? "Changes apply to new orders only. Existing orders keep their snapshot." : "New deals are saved as drafts."} />
      <form onSubmit={save} className="space-y-6">
        <Card className="space-y-4 p-5">
          <h2 className="font-heading text-lg font-semibold">Basic details</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Product / service name" required><Input data-testid="deal-name-input" required value={f.name} onChange={set("name")} className="h-11" /></Field>
            <Field label="Category" required><Input data-testid="deal-category-input" required value={f.category} onChange={set("category")} placeholder="e.g. Solar & Energy" className="h-11" /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Brand name" hint="Shown at the top of shareable PDFs and posters (instead of CommissionWorld)"><Input data-testid="deal-brand-name-input" value={f.brand_name} onChange={set("brand_name")} placeholder="e.g. SunPower Solar" className="h-11" /></Field>
            <Field label="Brand logo (optional)" hint="PNG with transparent background works best">
              <div className="flex items-center gap-3">
                {f.brand_logo && <img src={mediaUrl(f.brand_logo)} alt="" className="h-11 w-20 rounded border bg-slate-800 object-contain p-1" />}
                <UploadButton accept="image/*" testid="upload-brand-logo" label={f.brand_logo ? "Replace" : "Upload logo"} onDone={(p) => setF((x) => ({ ...x, brand_logo: p }))} />
                {f.brand_logo && <button type="button" className="text-xs text-red-600 underline" onClick={() => setF({ ...f, brand_logo: "" })} data-testid="remove-brand-logo">remove</button>}
              </div>
            </Field>
          </div>
          <Field label="Description"><Textarea data-testid="deal-description-input" rows={5} value={f.description} onChange={set("description")} /></Field>
        </Card>
        <Card className="space-y-4 p-5">
          <h2 className="font-heading text-lg font-semibold">Price and commission</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Fixed selling price (PKR)" required hint="Whole rupees"><Input data-testid="deal-price-input" type="number" min={1} step={1} required value={f.price} onChange={set("price")} className="h-11" /></Field>
            <Field label="Commission type" required>
              <Select value={f.commission_type} onValueChange={(v) => setF({ ...f, commission_type: v })}>
                <SelectTrigger className="h-11" data-testid="deal-commission-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="fixed">Fixed amount per unit (PKR)</SelectItem><SelectItem value="percent">Percentage of order value</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label={f.commission_type === "fixed" ? "Commission (PKR per unit)" : "Commission (%)"} required>
              <Input data-testid="deal-commission-value" type="number" min={0} step={f.commission_type === "fixed" ? 1 : 0.01} required value={f.commission_value} onChange={set("commission_value")} className="h-11" />
            </Field>
          </div>
          <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="deal-commission-preview">Customer pays <b>{pkr(price)}</b> · Executive earns <b>{pkr(preview)}</b> per unit (rounded to nearest rupee, half up)</p>
        </Card>
        <Card className="space-y-4 p-5">
          <h2 className="font-heading text-lg font-semibold">Availability and delivery</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Availability">
              <Select value={f.availability} onValueChange={(v) => setF({ ...f, availability: v })}>
                <SelectTrigger className="h-11" data-testid="deal-availability"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="in_stock">In stock</SelectItem><SelectItem value="limited">Limited stock</SelectItem><SelectItem value="out_of_stock">Out of stock</SelectItem><SelectItem value="on_request">Available on request</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Stock quantity" hint="Leave empty if not tracked"><Input data-testid="deal-stock-input" type="number" min={0} value={f.stock_qty} onChange={set("stock_qty")} className="h-11" /></Field>
            <Field label="Requires delivery address"><div className="flex h-11 items-center"><Switch data-testid="deal-requires-delivery" checked={f.requires_delivery} onCheckedChange={(v) => setF({ ...f, requires_delivery: v })} /></div></Field>
          </div>
          <Field label="Delivery / fulfilment information"><Textarea data-testid="deal-delivery-input" rows={3} value={f.delivery_info} onChange={set("delivery_info")} /></Field>
          <Field label="Terms and conditions"><Textarea data-testid="deal-terms-input" rows={3} value={f.terms} onChange={set("terms")} /></Field>
        </Card>
        <Card className="space-y-4 p-5">
          <h2 className="font-heading text-lg font-semibold">Images and video</h2>
          <Field label="Main image" hint="Upload a file or paste an image link">
            <div className="flex flex-wrap items-center gap-3">
              {f.main_image && <img src={mediaUrl(f.main_image)} alt="" className="h-20 w-32 rounded-lg object-cover" />}
              <UploadButton accept="image/*" testid="upload-main-image" label="Upload image" onDone={(p) => setF((x) => ({ ...x, main_image: p }))} />
            </div>
            <Input data-testid="deal-main-image-url" placeholder="https://… (optional)" value={f.main_image.startsWith("http") ? f.main_image : ""} onChange={set("main_image")} className="mt-2 h-11" />
          </Field>
          <Field label="Additional images / banners">
            <div className="flex flex-wrap items-center gap-3">
              {f.images.map((p) => (
                <div key={p} className="relative"><img src={mediaUrl(p)} alt="" className="h-20 w-28 rounded-lg object-cover" />
                  <button type="button" onClick={() => setF({ ...f, images: f.images.filter((x) => x !== p) })} className="absolute -right-2 -top-2 rounded-full bg-white p-1 shadow"><X className="h-3 w-3" /></button></div>
              ))}
              <UploadButton accept="image/*" testid="upload-extra-image" label="Add image" onDone={(p) => setF((x) => ({ ...x, images: [...x.images, p] }))} />
            </div>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Promotional video link" hint="YouTube or any video URL"><Input data-testid="deal-video-url" value={f.video_url} onChange={set("video_url")} className="h-11" /></Field>
            <Field label="Or upload a video" hint="MP4 / WEBM / MOV, max 50 MB">
              <div className="flex items-center gap-3">
                <UploadButton accept="video/*" testid="upload-video" label="Upload video" onDone={(p) => setF((x) => ({ ...x, video_file: p }))} />
                {f.video_file && <span className="text-xs text-emerald-700">Video attached <button type="button" className="ml-1 underline" onClick={() => setF({ ...f, video_file: "" })}>remove</button></span>}
              </div>
            </Field>
          </div>
        </Card>
        <div className="flex gap-3"><Button data-testid="deal-save-button" disabled={busy} className="h-12 px-8">{busy ? "Saving…" : "Save deal"}</Button><Button type="button" variant="outline" className="h-12" onClick={() => nav("/admin/deals")}>Cancel</Button></div>
      </form>
    </div>
  );
}
